import { FC, useRef, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

export type TableToolbarSearchType = {
  keepExpanded?: boolean;
  onSearch: (_searchValue: any) => void;
  clearCallbackRef?: React.RefObject<any>;
  initialValue?: string;
};

export const TableToolbarSearch: FC<TableToolbarSearchType> = ({ keepExpanded = false, onSearch, clearCallbackRef, initialValue }) => {
  const [expanded, setExpanded] = useState(keepExpanded);
  const [searchValue, setSearchValue] = useState(initialValue ?? "");

  const inputRef = useRef<any>(null);
  const debounceTimer = useRef(null as NodeJS.Timeout | null);

  const handleExpand = () => {
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleBlur = (e: any) => {
    if (!keepExpanded && !e.currentTarget.contains(e.relatedTarget) && !searchValue) {
      setExpanded(false);
    }
  };

  const handleClear = (fromClearCallback?: boolean) => {
    setSearchValue("");
    onSearch("");

    if (!fromClearCallback) {
      inputRef.current?.focus();
    }
  };

  if (clearCallbackRef) {
    clearCallbackRef.current = () => handleClear(true);
  }

  return (
    <Box onBlur={handleBlur} sx={{ display: "grid", alignItems: "center", position: "relative" }}>
      <Tooltip title="Search">
        <IconButton
          onClick={handleExpand}
          sx={{
            gridArea: "1 / 1",
            width: "fit-content",
            zIndex: expanded ? 0 : 1,
            opacity: expanded ? 0 : 1,
            pointerEvents: expanded ? "none" : "auto",
            transition: "opacity 0.2s",
          }}
        >
          <SearchIcon />
        </IconButton>
      </Tooltip>
      <TextField
        inputRef={inputRef}
        onChange={(e) => {
          setSearchValue(e.target.value);

          if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
          }

          debounceTimer.current = setTimeout(() => {
            onSearch(e.target.value.toLowerCase().replace(/\s/g, ""));
          }, 500);
        }}
        placeholder="Search..."
        size="small"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchValue && (
                  <Tooltip title="Clear">
                    <IconButton onClick={() => handleClear()} size="small" sx={{ padding: "2px" }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          },
        }}
        sx={{
          gridArea: "1 / 1",
          width: (theme) => (expanded ? theme.spacing(30) : 0),
          opacity: expanded ? 1 : 0,
          transition: "width 0.3s, opacity 0.2s",
          "& .MuiInputBase-input": {
            padding: 0,
          },
        }}
        value={searchValue}
        variant="outlined"
      />
    </Box>
  );
};
